import React from 'react';
import type { Vehicle } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { getVehicleBrandLabel, useDriverDrawerStore } from '@hero/hooks-shared';
import { DrawerContentCard } from '../../../app/Drawer/DrawerContentCard';
import { EyeIcon } from '@hero/web-icons';
import { FileThumbnail } from '../../../app/File/shared/FileThumbnail';
import { VehiclePlateDisplay } from '../../../app/VehiclePlateDisplay';
import {
  AvailabilityChip,
  vehicleStatusChip,
} from '../../../app/Select/SelectAsync/selectorEntityRowUtils';

export interface VehicleSummaryTabProps {
  vehicle: Vehicle;
}

export function VehicleSummaryTab({ vehicle }: VehicleSummaryTabProps) {
  const { t } = useTranslation(['m-vehicle']);
  const openDriverDrawer = useDriverDrawerStore(s => s.open);

  const statusChip = vehicleStatusChip(t, vehicle.status);
  const regImageSrc = vehicle.registration?.image?.url ?? vehicle.registration?.image?.upload ?? '';
  const hasVehiclePhotos = vehicle.images && vehicle.images.length > 0;
  const showMediaRow = hasVehiclePhotos || !!regImageSrc;

  const driverCard = vehicle.driver ? (
    <button
      type="button"
      className="flex items-center gap-2 text-primary hover:underline"
      onClick={() => openDriverDrawer(String(vehicle.driver!.driverId))}>
      <span>{vehicle.driver.name}</span>
      <EyeIcon className="h-3.5 w-3.5" />
    </button>
  ) : (
    <span className="text-muted-foreground">{t('drawer.no_driver_assigned')}</span>
  );

  return (
    <div className="flex flex-col gap-4 p-5">
      <DrawerContentCard
        title={t('drawer.tabs.summary')}
        list={[
          {
            label: t('drawer.fields.plate'),
            value: <VehiclePlateDisplay plate={vehicle.registration.plate} />,
          },
          {
            label: t('drawer.fields.type'),
            value: t(`types.${vehicle.classification.type}`, {
              defaultValue: vehicle.classification.type,
            }),
          },
          {
            label: t('drawer.fields.brand'),
            value: getVehicleBrandLabel(vehicle.classification.brand, t),
          },
          { label: t('drawer.fields.year'), value: vehicle.classification.year?.toString() || '-' },
          {
            label: t('drawer.fields.color'),
            value: vehicle.classification.color || '-',
            hidden: !vehicle.classification.color,
          },
          {
            label: t('drawer.fields.country'),
            value: t(`countries.${vehicle.registration.country}`, {
              defaultValue: vehicle.registration.country,
            }),
          },
          {
            label: t('table.columns.status'),
            value: <AvailabilityChip label={statusChip.label} tone={statusChip.tone} />,
          },
        ]}
      />

      <DrawerContentCard
        title={t('drawer.fields.driver')}
        list={[{ label: t('drawer.fields.driver'), value: driverCard }]}
      />

      {showMediaRow && (
        <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
          <div className="flex flex-wrap items-start gap-6">
            {hasVehiclePhotos && (
              <div className="flex min-w-0 flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('drawer.fields.vehicle_images')}
                </span>
                <div className="flex flex-wrap gap-3 overflow-x-auto pb-1">
                  {vehicle.images!.map((img, idx) => {
                    const src = img.url ?? img.upload ?? '';
                    if (!src) return null;
                    return (
                      <FileThumbnail
                        key={idx}
                        src={src}
                        size="xl"
                        expandable
                        className="max-w-[200px] shrink-0 rounded-lg"
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {regImageSrc ? (
              <div className="flex min-w-0 flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('drawer.fields.registration')}
                </span>
                <FileThumbnail
                  src={regImageSrc}
                  size="xl"
                  expandable
                  className="max-w-[280px] rounded-lg"
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
